/**
 * Servicio para gestión del historial de compras
 * Este archivo maneja la obtención y procesamiento de transacciones de licencias
 * Se utiliza para alimentar la vista de Historial de Compras
 * 
 * ═══════════════════════════════════════════════════════════════════════
 * INSTRUCCIONES PARA INTEGRACIÓN CON BACKEND
 * ═══════════════════════════════════════════════════════════════════════
 * 
 * ENDPOINTS REQUERIDOS:
 * ---------------------
 * 1. GET /api/purchases/history - Historial paginado de compras
 * 2. GET /api/purchases/summary - Resumen de estadísticas
 * 3. GET /api/purchases/{id}/details - Detalles de una compra
 * 4. GET /api/invoices/{number}/download - Descargar factura PDF
 * 
 * HEADERS NECESARIOS:
 * -------------------
 * - Authorization: Bearer {token}
 * - Content-Type: application/json
 * 
 * CONFIGURACIÓN:
 * --------------
 * - URL del API: Variable de entorno REACT_APP_API_URL
 * - Token: Se obtiene de localStorage.getItem('token')
 * 
 * CAMBIOS PARA PRODUCCIÓN:
 * ------------------------
 * 1. En cada función (getPurchaseHistory, getPurchaseSummary, etc):
 *    - ELIMINAR: return getMockXXX();
 *    - ⚠️ DESCOMENTAR: throw error;
 * 
 * VER DOCUMENTACIÓN COMPLETA EN: GUIA_HISTORIAL_COMPRAS.md
 * ═══════════════════════════════════════════════════════════════════════
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL_RENDER || 'http://localhost:8000/api';

/**
 * Obtiene el token de autenticación desde localStorage
 * @returns {string|null} Token de acceso o null si no existe
 */
const getAuthToken = () => {
  return localStorage.getItem('access_token');
};

/**
 * Obtiene los headers necesarios para las peticiones autenticadas
 * @returns {Object} Headers con autenticación
 */
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Mapea estados del backend a estados del frontend
 * @param {string} backendStatus - Estado del backend (pendiente, aprobado, rechazado, devuelto)
 * @returns {string} Estado del frontend (pending, completed, failed, refunded)
 */
const mapBackendStatusToFrontend = (backendStatus) => {
  const statusMap = {
    'pendiente': PURCHASE_STATUS.PENDING,
    'aprobado': PURCHASE_STATUS.COMPLETED,
    'rechazado': PURCHASE_STATUS.FAILED,
    'devuelto': PURCHASE_STATUS.REFUNDED,
  };
  return statusMap[backendStatus?.toLowerCase()] || PURCHASE_STATUS.PENDING;
};

/**
 * Estados posibles de una compra
 */
export const PURCHASE_STATUS = {
  COMPLETED: 'completed',
  PENDING: 'pending',
  FAILED: 'failed',
  REFUNDED: 'refunded',
};

/**
 * Tipos de compra (Planes)
 */
export const PURCHASE_TYPE = {
  BASIC: 'basic',
  INTERMEDIATE: 'intermediate',
  PREMIUM: 'premium',
};

/**
 * Configuración de planes
 */
export const PLAN_CONFIG = {
  [PURCHASE_TYPE.BASIC]: {
    name: 'Plan Básico',
    description: 'Ideal para pequeñas empresas',
    price: 99000,
    evaluations: 10,
  },
  [PURCHASE_TYPE.INTERMEDIATE]: {
    name: 'Plan Intermedio',
    description: 'Para empresas medianas con procesos de selección regulares',
    price: 219000,
    evaluations: 50,
  },
  [PURCHASE_TYPE.PREMIUM]: {
    name: 'Plan Premium',
    description: 'Ideal para empresas grandes con alto volumen de evaluaciones',
    price: 389000,
    evaluations: 99,
  },
};

/**
 * Configuración de colores e iconos por estado
 */
export const STATUS_CONFIG = {
  [PURCHASE_STATUS.COMPLETED]: {
    color: 'success',
    icon: 'check_circle',
    label: 'Completada',
    variant: 'gradient',
  },
  [PURCHASE_STATUS.PENDING]: {
    color: 'warning',
    icon: 'pending',
    label: 'Pendiente',
    variant: 'gradient',
  },
  [PURCHASE_STATUS.FAILED]: {
    color: 'error',
    icon: 'cancel',
    label: 'Fallida',
    variant: 'gradient',
  },
  [PURCHASE_STATUS.REFUNDED]: {
    color: 'info',
    icon: 'replay',
    label: 'Reembolsada',
    variant: 'gradient',
  },
};

/**
 * Configuración de iconos por tipo de compra (plan)
 */
export const TYPE_CONFIG = {
  [PURCHASE_TYPE.BASIC]: {
    icon: 'workspace_premium',
    label: 'Plan Básico',
  },
  [PURCHASE_TYPE.INTERMEDIATE]: {
    icon: 'stars',
    label: 'Plan Intermedio',
  },
  [PURCHASE_TYPE.PREMIUM]: {
    icon: 'diamond',
    label: 'Plan Premium',
  },
};

/**
 * Obtiene el historial de compras con paginación y filtros
 * 
 * @param {Object} params - Parámetros de consulta
 * @param {number} params.page - Número de página (default: 1)
 * @param {number} params.limit - Registros por página (default: 10)
 * @param {string} params.dateFrom - Fecha inicio (YYYY-MM-DD)
 * @param {string} params.dateTo - Fecha fin (YYYY-MM-DD)
 * @param {string} params.status - Estado (pendiente|aprobado|rechazado|devuelto)
 * @param {string} params.search - Búsqueda en ref_transaccion u observaciones
 * @returns {Promise<Object>} Historial de compras paginado
 */
export const getPurchaseHistory = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Agregar parámetros opcionales
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);

    const response = await fetch(
      `${API_BASE_URL}/api/purchases/history?${queryParams.toString()}`,
      {
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error('Error al obtener historial de compras');
    }

    const result = await response.json();
    console.log('📊 Historial recibido del backend:', result);
    
    // El backend ya retorna {success, data}, extraer data directamente
    const backendData = result.data || result;
    
    // Mapear respuesta del backend al formato del frontend
    return {
      success: true,
      data: {
        purchases: backendData.purchases?.map(purchase => ({
          id: purchase.pago_id,
          orderNumber: purchase.ref_transaccion,
          date: purchase.fecha_pago || purchase.created_at,
          total_amount: purchase.precio_total,
          status: mapBackendStatusToFrontend(purchase.estado_aprobacion),
          packageName: purchase.paquetes && purchase.paquetes.length > 0 
            ? purchase.paquetes.map(p => p.name).join(', ') 
            : 'N/A',
          licenses: purchase.total_licencias || 0,
          packages: purchase.paquetes || [],
        })) || [],
        pagination: backendData.pagination || {
          total: 0,
          page: params.page || 1,
          limit: params.limit || 15,
          totalPages: 0
        }
      }
    };
  } catch (error) {
    console.error('Error en getPurchaseHistory:', error);
    throw error;
  }
};

/**
 * Obtiene el resumen de estadísticas de compras
 * @returns {Promise<Object>} Resumen de compras
 */
export const getPurchaseSummary = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/purchases/summary`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Error al obtener resumen de compras');
    }

    const result = await response.json();
    console.log('📈 Resumen recibido del backend:', result);
    
    // El backend ya retorna {success, data}, extraer data directamente
    const data = result.data || result;
    
    // Mapear respuesta del backend al formato del frontend
    return {
      success: true,
      data: {
        total_purchases: data.total_compras || 0,
        total_amount: data.total_monto || 0,
        by_status: {
          completed: data.aprobadas || 0,
          pending: data.pendientes || 0,
          failed: data.rechazadas || 0,
          refunded: data.devueltas || 0,
        },
        recent_purchases: data.recientes || 0,
      }
    };
  } catch (error) {
    console.error('Error en getPurchaseSummary:', error);
    throw error;
  }
};

/**
 * Obtiene los detalles completos de una compra específica
 * @param {string} purchaseId - ID de la compra (pago_id)
 * @returns {Promise<Object>} Detalles de la compra
 */
export const getPurchaseDetails = async (purchaseId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/purchases/${purchaseId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Error al obtener detalles de compra');
    }

    const result = await response.json();
    console.log('🔍 Detalles recibidos del backend:', result);
    
    // El backend ya retorna {success, data}, extraer data directamente
    const data = result.data || result;
    
    // Mapear respuesta del backend al formato del frontend
    return {
      success: true,
      data: {
        id: data.pago_id,
        orderNumber: data.ref_transaccion,
        date: data.created_at,
        total_amount: data.precio_total,
        currency: data.moneda || 'COP',
        status: mapBackendStatusToFrontend(data.estado_aprobacion),
        payment_method: data.metodo_pago || 'TRANSFERENCIA',
        licenses: data.total_licencias || 0,
        packages: data.paquetes?.map(pkg => ({
          name: pkg.nombre,
          quantity: pkg.cantidad,
          licensesPerPackage: pkg.cant_licencias,
          totalLicenses: pkg.cantidad * pkg.cant_licencias,
          price: pkg.precio_unitario
        })) || [],
        company: {
          name: data.nombre_empresa || 'N/A',
          nit: data.nit_empresa || 'N/A'
        },
        observaciones: data.observaciones,
        fecha_aprobacion: data.fecha_aprobacion,
        intentos_pasarela: data.intentos_pasarela || []
      }
    };
  } catch (error) {
    console.error('Error en getPurchaseDetails:', error);
    throw error;
  }
};

/**
 * Descarga la factura de una compra
 * @param {string} invoiceNumber - Número de factura
 * @returns {Promise<Blob>} Archivo PDF
 */
export const downloadInvoice = async (invoiceNumber) => {
  try {
    const response = await fetch(`${API_BASE_URL}/invoices/${invoiceNumber}/download`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      throw new Error('Error al descargar factura');
    }

    return await response.blob();
  } catch (error) {
    console.error('Error en downloadInvoice:', error);
    throw error;
  }
};

// ============================================================================
// FUNCIONES HELPER
// ============================================================================

/**
 * Formatea un monto a moneda local
 * @param {number} amount - Monto a formatear
 * @param {string} currency - Código de moneda (default: COP)
 * @returns {string} Monto formateado
 */
export const formatCurrency = (amount, currency = 'COP') => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Formatea una fecha a formato legible
 * @param {string} isoDate - Fecha en formato ISO
 * @param {boolean} includeTime - Si incluir hora (default: false)
 * @returns {string} Fecha formateada
 */
export const formatDate = (isoDate, includeTime = false) => {
  const date = new Date(isoDate);
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
  }

  return date.toLocaleDateString('es-ES', options);
};

/**
 * Formatea una fecha a formato corto
 * @param {string} isoDate - Fecha en formato ISO
 * @returns {string} Fecha formateada (DD/MM/YYYY)
 */
export const formatDateShort = (isoDate) => {
  const date = new Date(isoDate);
  return date.toLocaleDateString('es-ES');
};

/**
 * Obtiene la configuración de un estado
 * @param {string} status - Estado de la compra
 * @returns {Object} Configuración del estado
 */
export const getStatusConfig = (status) => {
  return STATUS_CONFIG[status] || STATUS_CONFIG[PURCHASE_STATUS.PENDING];
};

/**
 * Obtiene la configuración de un tipo de compra
 * @param {string} type - Tipo de compra
 * @returns {Object} Configuración del tipo
 */
export const getTypeConfig = (type) => {
  return TYPE_CONFIG[type] || TYPE_CONFIG[PURCHASE_TYPE.NEW];
};

/**
 * Filtra compras por criterios locales (útil para datos mock)
 * @param {Array} purchases - Array de compras
 * @param {Object} filters - Filtros a aplicar
 * @returns {Array} Compras filtradas
 */
export const filterPurchases = (purchases, filters) => {
  let filtered = [...purchases];

  if (filters.dateFrom) {
    const fromDate = new Date(filters.dateFrom);
    filtered = filtered.filter(p => new Date(p.date) >= fromDate);
  }

  if (filters.dateTo) {
    const toDate = new Date(filters.dateTo);
    filtered = filtered.filter(p => new Date(p.date) <= toDate);
  }

  if (filters.status) {
    filtered = filtered.filter(p => p.status === filters.status);
  }

  if (filters.type) {
    filtered = filtered.filter(p => p.type === filters.type);
  }

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(p => 
      p.orderNumber.toLowerCase().includes(searchLower) ||
      p.description.toLowerCase().includes(searchLower)
    );
  }

  return filtered;
};

/**
 * Pagina un array de compras
 * @param {Array} purchases - Array de compras
 * @param {number} page - Número de página
 * @param {number} limit - Elementos por página
 * @returns {Object} Datos paginados
 */
export const paginatePurchases = (purchases, page = 1, limit = 10) => {
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedData = purchases.slice(startIndex, endIndex);

  return {
    purchases: paginatedData,
    pagination: {
      currentPage: page,
      totalPages: Math.ceil(purchases.length / limit),
      totalRecords: purchases.length,
      hasNext: endIndex < purchases.length,
      hasPrev: page > 1,
    },
  };
};

// ============================================================================
// DATOS MOCK PARA DESARROLLO Y PRUEBAS
// ⚠️ EN PRODUCCIÓN: Estos datos NO se usarán si el backend funciona correctamente
// ============================================================================

/**
 * Genera datos mock de compras
 */
const generateMockPurchases = () => {
  const purchases = [];
  const today = new Date();

  const mockData = [
    { months: 0, days: 5, plan: 'intermediate', status: 'completed', evaluations: 50, amount: 219000 },
    { months: 0, days: 12, plan: 'basic', status: 'completed', evaluations: 10, amount: 99000 },
    { months: 1, days: 3, plan: 'premium', status: 'completed', evaluations: 99, amount: 389000 },
    { months: 1, days: 18, plan: 'basic', status: 'completed', evaluations: 10, amount: 99000 },
    { months: 2, days: 7, plan: 'intermediate', status: 'completed', evaluations: 50, amount: 219000 },
    { months: 2, days: 22, plan: 'basic', status: 'pending', evaluations: 10, amount: 99000 },
    { months: 3, days: 5, plan: 'premium', status: 'completed', evaluations: 99, amount: 389000 },
    { months: 3, days: 14, plan: 'basic', status: 'failed', evaluations: 10, amount: 99000 },
    { months: 4, days: 2, plan: 'intermediate', status: 'completed', evaluations: 50, amount: 219000 },
    { months: 4, days: 19, plan: 'basic', status: 'refunded', evaluations: 10, amount: 99000 },
    { months: 5, days: 8, plan: 'premium', status: 'completed', evaluations: 99, amount: 389000 },
    { months: 5, days: 25, plan: 'intermediate', status: 'completed', evaluations: 50, amount: 219000 },
  ];

  mockData.forEach((data, index) => {
    const purchaseDate = new Date(today);
    purchaseDate.setMonth(purchaseDate.getMonth() - data.months);
    purchaseDate.setDate(purchaseDate.getDate() - data.days);

    const orderNum = `ORD-${purchaseDate.getFullYear()}${String(purchaseDate.getMonth() + 1).padStart(2, '0')}${String(index + 1).padStart(4, '0')}`;
    const purId = `PUR-${purchaseDate.getFullYear()}-${String(index + 1).padStart(3, '0')}`;
    
    const planConfig = PLAN_CONFIG[data.plan];

    purchases.push({
      id: purId,
      orderNumber: orderNum,
      date: purchaseDate.toISOString(),
      type: data.plan,
      status: data.status,
      amount: data.amount,
      currency: 'COP',
      licensesCount: data.evaluations,
      paymentMethod: 'credit_card',
      lastFourDigits: '4242',
      description: `${planConfig.name} - ${data.evaluations} evaluaciones`,
      invoice: {
        number: `FAC-${purchaseDate.getFullYear()}-${String(index + 1).padStart(3, '0')}`,
        downloadUrl: `/api/invoices/FAC-${purchaseDate.getFullYear()}-${String(index + 1).padStart(3, '0')}/download`,
      },
      billingInfo: {
        name: 'Empresa XYZ S.A.S.',
        rfc: 'NIT 900123456-1',
        address: 'Calle 100 # 19-45, Oficina 501',
        city: 'Bogotá D.C.',
        zipCode: '110111',
      },
    });
  });

  return purchases.sort((a, b) => new Date(b.date) - new Date(a.date));
};

const MOCK_PURCHASES = generateMockPurchases();

/**
 * Mock: Historial de compras
 */
const getMockPurchaseHistory = (params = {}) => {
  let filtered = filterPurchases(MOCK_PURCHASES, params);
  const paginated = paginatePurchases(filtered, params.page || 1, params.limit || 10);

  return {
    success: true,
    data: paginated,
  };
};

/**
 * Mock: Resumen de compras
 */
const getMockPurchaseSummary = () => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const totalSpent = MOCK_PURCHASES
    .filter(p => p.status === PURCHASE_STATUS.COMPLETED)
    .reduce((sum, p) => sum + p.amount, 0);

  const totalLicenses = MOCK_PURCHASES
    .filter(p => p.status === PURCHASE_STATUS.COMPLETED)
    .reduce((sum, p) => sum + p.licensesCount, 0);

  const monthlyPurchases = MOCK_PURCHASES.filter(p => {
    const pDate = new Date(p.date);
    return pDate.getMonth() === currentMonth && pDate.getFullYear() === currentYear;
  });

  const monthlySpent = monthlyPurchases
    .filter(p => p.status === PURCHASE_STATUS.COMPLETED)
    .reduce((sum, p) => sum + p.amount, 0);

  const lastPurchase = MOCK_PURCHASES[0];

  return {
    success: true,
    data: {
      totalSpent,
      totalPurchases: MOCK_PURCHASES.length,
      totalLicensesPurchased: totalLicenses,
      lastPurchase: lastPurchase ? {
        date: lastPurchase.date,
        amount: lastPurchase.amount,
        licensesCount: lastPurchase.licensesCount,
      } : null,
      monthlyStats: {
        currentMonth: {
          spent: monthlySpent,
          purchases: monthlyPurchases.length,
          licenses: monthlyPurchases.reduce((sum, p) => sum + p.licensesCount, 0),
        },
      },
    },
  };
};

/**
 * Mock: Detalles de compra
 */
const getMockPurchaseDetails = (purchaseId) => {
  const purchase = MOCK_PURCHASES.find(p => p.id === purchaseId);

  if (!purchase) {
    throw new Error('Compra no encontrada');
  }

  return {
    success: true,
    data: purchase,
  };
};

export default {
  getPurchaseHistory,
  getPurchaseSummary,
  getPurchaseDetails,
  downloadInvoice,
  formatCurrency,
  formatDate,
  formatDateShort,
  getStatusConfig,
  getTypeConfig,
  filterPurchases,
  paginatePurchases,
  PURCHASE_STATUS,
  PURCHASE_TYPE,
  STATUS_CONFIG,
  TYPE_CONFIG,
};
